import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Upload, Trash2, Save, Eye, EyeOff } from "lucide-react";
import {
  uploadLogo,
  deleteLogo,
  logoUrl,
  getAppSettings,
  updateAppSettings,
  listTemplates,
} from "@/lib/api";
import { toast } from "sonner";
import TemplatePicker from "@/components/TemplatePicker";
import AccentColors from "@/components/AccentColors";

function Toggle({ checked, onChange, label, hint, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className="flex items-start gap-3 text-left group"
    >
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 border ${
          checked
            ? "bg-[var(--brand-primary)] border-[var(--brand-primary)]"
            : "bg-white border-[var(--border-default)]"
        } transition-colors`}
      >
        <span
          className={`absolute top-[2px] h-[18px] w-[18px] bg-white transition-transform ${
            checked
              ? "translate-x-[22px] border border-[var(--brand-primary)]"
              : "translate-x-[2px] border border-[var(--border-default)]"
          }`}
        />
      </span>
      <span className="flex-1">
        <span className="block text-sm font-semibold text-[var(--text-primary)] group-hover:text-[var(--brand-primary)] transition-colors">
          {label}
        </span>
        {hint && (
          <span className="block text-xs text-[var(--text-secondary)] mt-0.5">
            {hint}
          </span>
        )}
      </span>
    </button>
  );
}

export default function BrandSettings() {
  // Logo state
  const [version, setVersion] = useState(Date.now());
  const [hasLogo, setHasLogo] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const inputRef = useRef(null);

  // App settings state
  const [settings, setSettings] = useState({
    footer_text: "",
    show_bullets: true,
    anonymize: false,
    template: "modern",
    template_accents: {},
  });
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetch(logoUrl(version))
      .then((r) => setHasLogo(r.ok))
      .catch(() => setHasLogo(false));
  }, [version]);

  useEffect(() => {
    // mount-only: imported API helpers + stable setters are not deps
    getAppSettings()
      .then((s) => {
        setSettings(s);
        setInitial(s);
      })
      .catch(() => toast.error("Failed to load settings"));
    listTemplates()
      .then(setTemplates)
      .catch(() => {});
     
  }, []);

  const dirty =
    initial &&
    (initial.footer_text !== settings.footer_text ||
      initial.show_bullets !== settings.show_bullets ||
      initial.anonymize !== settings.anonymize ||
      initial.template !== settings.template ||
      JSON.stringify(initial.template_accents || {}) !==
        JSON.stringify(settings.template_accents || {}));

  const handleLogoFile = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g|webp)$/i.test(file.name)) {
      toast.error("Use PNG, JPG, or WEBP");
      return;
    }
    setUploadingLogo(true);
    try {
      await uploadLogo(file);
      setVersion(Date.now());
      toast.success("Logo updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm("Remove current logo?")) return;
    try {
      await deleteLogo();
      setHasLogo(false);
      setVersion(Date.now());
      toast.success("Logo removed");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const updated = await updateAppSettings(settings);
      setSettings(updated);
      setInitial(updated);
      toast.success("Brand settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="swiss-card p-6 md:p-8 space-y-8" data-testid="brand-settings-card">
      {/* Logo row */}
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="md:w-48 flex flex-col items-center md:items-start">
          <div className="overline mb-3">brand · logo</div>
          <div
            className="w-44 h-24 border border-[var(--border-default)] bg-white flex items-center justify-center overflow-hidden"
            data-testid="logo-preview"
          >
            {hasLogo ? (
              <img
                src={logoUrl(version)}
                alt="company logo"
                className="max-w-full max-h-full object-contain p-2"
              />
            ) : (
              <ImageIcon
                className="w-8 h-8 text-[var(--text-muted)]"
                strokeWidth={1.2}
              />
            )}
          </div>
        </div>

        <div className="flex-1">
          <h3 className="font-heading text-xl font-bold tracking-tight mb-2">
            COMPANY LOGO
          </h3>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4 max-w-xl">
            Stamped top-right on every page of PDF and DOCX outputs. Wide
            horizontal logos render best (PNG with transparent background
            recommended).
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="logo-file-input"
              onChange={(e) => handleLogoFile(e.target.files?.[0])}
            />
            <button
              onClick={() => inputRef.current?.click()}
              className="btn-primary"
              disabled={uploadingLogo}
              data-testid="logo-upload-button"
            >
              {uploadingLogo ? (
                <span className="spinner" />
              ) : (
                <Upload className="w-4 h-4" strokeWidth={1.5} />
              )}
              {hasLogo ? "REPLACE LOGO" : "UPLOAD LOGO"}
            </button>
            {hasLogo && (
              <button
                onClick={handleLogoDelete}
                className="btn-secondary"
                data-testid="logo-delete-button"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                REMOVE
              </button>
            )}
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
              PNG · JPG · WEBP · max 5MB
            </span>
          </div>
        </div>
      </div>

      <div className="border-t border-[var(--border-default)]" />

      {/* Footer text + toggles */}
      <div className="grid md:grid-cols-[1fr_1fr] gap-8">
        <div>
          <div className="overline mb-3">brand · footer</div>
          <h3 className="font-heading text-xl font-bold tracking-tight mb-2">
            PAGE FOOTER
          </h3>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-3">
            Shown centered at font size 10 on every page of generated PDFs and
            DOCX documents.
          </p>
          <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1.5">
            Footer text
          </label>
          <input
            type="text"
            value={settings.footer_text || ""}
            onChange={(e) =>
              setSettings((s) => ({ ...s, footer_text: e.target.value }))
            }
            placeholder="e.g. ITHR Technologies Consulting LLC"
            className="swiss-input"
            data-testid="footer-text-input"
          />
        </div>

        <div>
          <div className="overline mb-3">brand · render</div>
          <h3 className="font-heading text-xl font-bold tracking-tight mb-2">
            OUTPUT BEHAVIOUR
          </h3>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
            Defaults applied to every PDF and DOCX render. Per-resume overrides
            still available on the detail page.
          </p>

          <div className="space-y-4">
            <Toggle
              checked={settings.show_bullets}
              onChange={(v) => setSettings((s) => ({ ...s, show_bullets: v }))}
              label="Show bullet points"
              hint="When off, only the AI role summaries are rendered (bullets hidden)."
              testId="toggle-show-bullets"
            />
            <Toggle
              checked={settings.anonymize}
              onChange={(v) => setSettings((s) => ({ ...s, anonymize: v }))}
              label="Anonymize candidate name (blind hire)"
              hint="Replaces the candidate's name with 'Candidate Profile'."
              testId="toggle-anonymize"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-[var(--border-default)]" />

      {/* Default template */}
      <div>
        <div className="overline mb-3">brand · default template</div>
        <h3 className="font-heading text-xl font-bold tracking-tight mb-2">
          DEFAULT TEMPLATE
        </h3>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4 max-w-2xl">
          Applied to every generated PDF and DOCX unless overridden on the
          resume detail page.
        </p>
        <TemplatePicker
          templates={templates}
          selected={settings.template}
          onChange={(t) => setSettings((s) => ({ ...s, template: t }))}
          label="Choose default"
        />
      </div>

      <div className="border-t border-[var(--border-default)]" />

      {/* Per-template accent colors */}
      <AccentColors
        templates={templates}
        accents={settings.template_accents || {}}
        onChange={(next) =>
          setSettings((s) => ({ ...s, template_accents: next }))
        }
      />

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--border-default)]">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
          {dirty ? "unsaved changes" : "all changes saved"}
        </span>
        <button
          onClick={saveSettings}
          disabled={!dirty || saving}
          className="btn-primary"
          data-testid="save-brand-settings-button"
        >
          {saving ? (
            <span className="spinner" />
          ) : (
            <Save className="w-4 h-4" strokeWidth={1.5} />
          )}
          SAVE SETTINGS
        </button>
      </div>
    </div>
  );
}
